import * as React from "react";
import { userHasPermission } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase H5d — single source of truth for the IA-side assessment
 * authoring write-gate.
 *
 * Combines two independent signals:
 *
 *   1. **RBAC** — the principal must hold `manage_question_bank`
 *      (either via the base role catalog or a designation override).
 *      This is the original Phase H5 FE-1 gate.
 *   2. **Tenant capability** — the SA must not have explicitly disabled
 *      `assessments_authoring_by_tenant` on this tenant. Default `True`
 *      (legacy tenant docs + freshly minted tenants are authoring-
 *      enabled out of the box).
 *
 * Returns three orthogonal booleans so consumers can render the right
 * surface for each rejection reason:
 *
 *   - `canAuthor` — overall write-eligibility. Hide / disable all
 *     authoring CTAs (`New template`, `Edit`, `Activate`, `Archive`,
 *     `Approve`, `Regenerate`, etc.) when this is `false`.
 *   - `missingRbac` — the user is logged in but lacks
 *     `manage_question_bank`. Surface the existing "ask your admin"
 *     banner.
 *   - `pausedByTenant` — the user has the RBAC but the SA has paused
 *     authoring for the tenant. Surface the H5d banner explaining the
 *     paused state + the account-manager contact path.
 *
 * `pausedByTenant` only fires when the user otherwise WOULD have been
 * able to author — i.e. when `missingRbac === false` — so the two
 * banners never stack. When neither banner fires, `canAuthor` is `true`
 * and the page renders its normal write surface.
 */
export interface AssessmentAuthoringGate {
  canAuthor: boolean;
  missingRbac: boolean;
  pausedByTenant: boolean;
}

export function useCanAuthorAssessments(): AssessmentAuthoringGate {
  const { user, featureFlags } = useAuth();
  return React.useMemo<AssessmentAuthoringGate>(() => {
    if (!user) {
      return { canAuthor: false, missingRbac: false, pausedByTenant: false };
    }
    const hasRbac = userHasPermission(
      user.role,
      user.permission_overrides ?? [],
      "manage_question_bank",
      user.permission_mode ?? "additive",
    );
    // Treat `null` (super_admin synthetic tenant, or pre-/auth/me)
    // and `undefined` as "no gating" — matches the BE contract.
    const flagEnabled = featureFlags?.assessments_authoring_by_tenant !== false;
    const missingRbac = !hasRbac;
    const pausedByTenant = hasRbac && !flagEnabled;
    return {
      canAuthor: hasRbac && flagEnabled,
      missingRbac,
      pausedByTenant,
    };
  }, [user, featureFlags]);
}
